import { Box } from "@mui/material"
import type { FC } from "react"
import { useEffect } from "react"
import { Controller, useFormContext } from "react-hook-form"

import { MAX_SUB_NUMBER_LENGTH } from "../../constants"
import {
  ACCOUNT_CLASSIFICATION_OPTIONS,
  ACCOUNT_TYPE_OPTIONS,
  COMPANY_OPTIONS,
  CURRENCY_OPTIONS,
  NORMAL_BALANCE_OPTIONS,
  REPORTING_ACCOUNT_TYPE_OPTIONS,
} from "../../constants/formOptions"
import type { GlAccountFormValues } from "../../schemas/glAccountForm"
import { getSubtypeOptionsForType } from "../../utils/formOptions"

import { ParentAccountAutocomplete } from "./ParentAccountAutocomplete"

import { FormNumericField } from "@/components/UiKit"
import { AerosTextField } from "@/components/UiKit/AerosTextField/AerosTextField"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import { ReportingAccountType } from "@/graphql/codegen/graphql"
import { classNames } from "@/utils/utils"

interface GlAccountFormProps {
  isEditMode?: boolean
  initialParentOption?: { value: string; label: string } | null
}

export const GlAccountForm: FC<GlAccountFormProps> = ({ isEditMode = false, initialParentOption }) => {
  const { control, watch, setValue } = useFormContext<GlAccountFormValues>()

  const reportingAccountType = watch("reportingAccountType")
  const selectedType = watch("type")
  const currentSubType = watch("subType")
  const isPostingAccount = reportingAccountType === ReportingAccountType.Posting
  const showFields = !!reportingAccountType

  const filteredSubtypeOptions = getSubtypeOptionsForType(selectedType)

  // Clear subType when it's invalid for the selected type (for Parent Accounts only)
  useEffect(() => {
    if (isPostingAccount || !currentSubType || !selectedType) return

    const isSubTypeValid = filteredSubtypeOptions.some((option) => option.value === currentSubType)
    if (!isSubTypeValid) {
      setValue("subType", null)
    }
  }, [selectedType, currentSubType, isPostingAccount, filteredSubtypeOptions, setValue])

  return (
    <Box className='grid grid-cols-2 gap-4'>
      <FormSelect
        control={control}
        name='reportingAccountType'
        label='Reporting Account Type'
        placeholder='Select reporting account type'
        options={REPORTING_ACCOUNT_TYPE_OPTIONS}
        required
        disabled={isEditMode}
        className='col-span-2'
        data-testid='reporting-account-type-input'
      />

      {showFields && (
        <>
          {isPostingAccount && <ParentAccountAutocomplete initialOption={initialParentOption} />}
          <FormSelect
            control={control}
            name='company'
            label='Company'
            options={COMPANY_OPTIONS}
            required
            disabled
            data-testid='company-input'
          />
          <FormNumericField
            control={control}
            name='costCenter'
            label='Cost Center'
            disabled={isEditMode}
            data-testid='cost-center-input'
          />
          <FormNumericField
            control={control}
            className={classNames({ "col-span-2": !isPostingAccount })}
            name='number'
            label='Account Number'
            required
            allowNegative={false}
            decimalScale={0}
            data-testid='account-number-input'
          />
          {isPostingAccount ? (
            <Controller
              control={control}
              name='subNumber'
              render={({ field, fieldState: { error } }) => (
                <AerosTextField
                  {...field}
                  value={field.value ?? ""}
                  onChange={(value) => field.onChange(value.replace(/\D/g, "").slice(0, MAX_SUB_NUMBER_LENGTH))}
                  onBlur={() => {
                    if (field.value?.length === 1) {
                      field.onChange(field.value.padStart(MAX_SUB_NUMBER_LENGTH, "0"))
                    }
                    field.onBlur()
                  }}
                  label='Sub Account Number'
                  required
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{ htmlInput: { maxLength: MAX_SUB_NUMBER_LENGTH, inputMode: "numeric" } }}
                  data-testid='sub-account-number-input'
                />
              )}
            />
          ) : null}
          <FormSelect
            control={control}
            name='type'
            label='Type'
            placeholder='Select type'
            options={ACCOUNT_TYPE_OPTIONS}
            required
            disabled={isPostingAccount}
            data-testid='type-input'
          />
          <FormSelect
            control={control}
            name='subType'
            label='Subtype'
            placeholder={selectedType ? "Select subtype" : "Select type first"}
            options={filteredSubtypeOptions}
            required
            disabled={isPostingAccount || !selectedType}
            data-testid='subtype-input'
          />
          <FormSelect
            control={control}
            name='classification'
            label='Classification'
            placeholder='Select classification'
            options={ACCOUNT_CLASSIFICATION_OPTIONS}
            required
            disabled={isPostingAccount}
            data-testid='classification-input'
          />
          <FormSelect
            control={control}
            name='currency'
            label='Currency'
            options={CURRENCY_OPTIONS}
            required
            disabled
            data-testid='currency-input'
          />
          <FormSelect
            className='col-span-2'
            control={control}
            name='balance'
            label='Normal Balance'
            placeholder='Select balance'
            options={NORMAL_BALANCE_OPTIONS}
            data-testid='normal-balance-input'
          />
          <FormTextField
            control={control}
            name='title'
            label='Account Title'
            required
            className='col-span-2'
            slotProps={{ htmlInput: { maxLength: 35 } }}
            data-testid='account-title-input'
          />
          <FormTextField
            control={control}
            name='description'
            label='Description'
            className='col-span-2'
            slotProps={{ htmlInput: { maxLength: 50 } }}
            data-testid='description-input'
          />
        </>
      )}
    </Box>
  )
}
